import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { useAuth } from "./AuthContext";
import { useCategories } from "./CategoryContext";
import { useGroups } from "./GroupContext";
import { RecipeProvider, useRecipes } from "./RecipeContext";
import { KITCHEN_ILLUSTRATIONS_ANIMATED } from "./config/features";
import { categoryCoverUrl, findCategoryCover } from "./data/categories";
import {
  filterIllustrations,
  findIllustration,
  illustrationCategories,
} from "./data/illustrations";
import {
  invitationState,
  inviteAllowedUser,
  listAllowedUsers,
  normalizeInviteEmail,
  setAllowedUserActive,
  validateInviteEmail,
  type AllowedUser,
} from "./lib/admin";
import { compressRecipePhoto } from "./lib/image";
import { createClientId } from "./lib/id";
import { emptyDraft, filterRecipes, reorder, totalMinutes, validateRecipe } from "./lib/recipes";
import type { IllustrationAsset, Ingredient, Recipe, RecipeDraft, RecipeFilters, RecipeStep } from "./types";

type Route =
  | { name: "home" }
  | { name: "recipe"; id: string }
  | { name: "edit"; id?: string }
  | { name: "cook"; id: string }
  | { name: "admin" }
  | { name: "categories" }
  | { name: "my-recipes" }
  | { name: "groups" }
  | { name: "group"; id: string }
  | { name: "profile" };

function routeFromHash(): Route {
  const parts = window.location.hash.replace(/^#\/?/, "").split("/").filter(Boolean);
  if (parts[0] === "recipe" && parts[1]) return { name: "recipe", id: parts[1] };
  if (parts[0] === "edit") return { name: "edit", id: parts[1] };
  if (parts[0] === "cook" && parts[1]) return { name: "cook", id: parts[1] };
  if (parts[0] === "admin") return { name: "admin" };
  if (parts[0] === "categories") return { name: "categories" };
  if (parts[0] === "my-recipes") return { name: "my-recipes" };
  if (parts[0] === "groups" && parts[1]) return { name: "group", id: parts[1] };
  if (parts[0] === "groups") return { name: "groups" };
  if (parts[0] === "profile") return { name: "profile" };
  return { name: "home" };
}

function navigate(path = "") {
  window.location.hash = path ? `#/${path}` : "#/";
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function useRoute() {
  const [route, setRoute] = useState(routeFromHash);
  useEffect(() => {
    const onHash = () => setRoute(routeFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  return route;
}

function initials(name: string) {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function AppHeader({ title, back }: { title?: string; back?: string }) {
  const { user } = useAuth();
  return (
    <header className="app-header">
      <div className="header-inner">
        {back !== undefined ? (
          <button className="icon-button" onClick={() => navigate(back)} aria-label="Retour">
            ←
          </button>
        ) : (
          <button className="brand" onClick={() => navigate()} aria-label="Accueil Mijoté">
            <span className="brand-mark">M</span>
            <span>Mijoté</span>
          </button>
        )}
        {title && <strong className="header-title">{title}</strong>}
        <button className="avatar" onClick={() => navigate("profile")} aria-label="Ouvrir mon profil">
          {user?.avatarUrl ? <img src={user.avatarUrl} alt="" /> : initials(user?.displayName ?? "M")}
        </button>
      </div>
    </header>
  );
}

function BottomNav({ current }: { current: "home" | "recipes" | "groups" | "profile" }) {
  return (
    <nav className="bottom-nav" aria-label="Navigation principale">
      <button className={current === "home" ? "active" : ""} onClick={() => navigate()}>
        <span aria-hidden="true">⌂</span>Accueil
      </button>
      <button className={current === "recipes" ? "active" : ""} onClick={() => navigate("my-recipes")}>
        <span aria-hidden="true">▤</span>Mes recettes
      </button>
      <button className={current === "groups" ? "active" : ""} onClick={() => navigate("groups")}>
        <span aria-hidden="true">◉</span>Cercles
      </button>
      <button className={current === "profile" ? "active" : ""} onClick={() => navigate("profile")}>
        <span aria-hidden="true">☺</span>Profil
      </button>
    </nav>
  );
}

function recipeAudienceLabel(recipe: Recipe) {
  if (recipe.visibility === "shared") return "Toute la famille";
  const audiences = [
    ...recipe.sharedGroups.map((group) => group.name),
    ...recipe.sharedWith.map((profile) => profile.displayName),
  ];
  if (audiences.length) return audiences.join(", ");
  return "Seulement moi";
}

function recipeVisibilityTag(recipe: Recipe) {
  if (recipe.visibility === "shared") return "👪 Toute la famille";
  if (recipe.sharedGroups.length && recipe.sharedWith.length) return "◎ Partage ciblé";
  if (recipe.sharedGroups.length) return "◉ Cercle privé";
  if (recipe.sharedWith.length) return "↗ Partage direct";
  return "🔒 Privée";
}

function Toast({ message, onClose }: { message: string; onClose: () => void }) {
  useEffect(() => {
    const timeout = window.setTimeout(onClose, 3500);
    return () => window.clearTimeout(timeout);
  }, [onClose]);
  return (
    <div className="toast" role="status">
      <span>✓</span>
      {message}
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="loading-screen" role="status">
      <span className="brand-mark large">M</span>
      <p>On prépare la cuisine…</p>
    </div>
  );
}

function RecipeVisual({ recipe, detail = false }: { recipe: Recipe; detail?: boolean }) {
  const { categories } = useCategories();
  const [photoFailed, setPhotoFailed] = useState(false);
  const fallback = findCategoryCover(recipe.categories, categories);

  useEffect(() => {
    setPhotoFailed(false);
  }, [recipe.photoUrl]);

  if (recipe.photoUrl && !photoFailed) {
    return (
      <img
        src={recipe.photoUrl}
        alt={detail ? `Plat terminé : ${recipe.title}` : ""}
        onError={() => setPhotoFailed(true)}
      />
    );
  }

  return (
    <div className="category-cover">
      <img src={fallback.url} alt="" />
      <span>{fallback.name}</span>
    </div>
  );
}

function LoginPage() {
  const { signIn, signUp, sendPasswordReset, isDemo } = useAuth();
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [email, setEmail] = useState(isDemo ? "claire@demo.mijote.fr" : "");
  const [password, setPassword] = useState(isDemo ? "mijote-demo" : "");
  const [displayName, setDisplayName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  function friendlyError(caught: unknown) {
    const message = caught instanceof Error ? caught.message : "";
    if (/invalid login credentials/i.test(message)) return "Adresse ou mot de passe incorrect. Vérifiez les deux champs.";
    if (/email not confirmed/i.test(message)) return "Votre adresse doit encore être confirmée depuis le courriel reçu.";
    if (/user already registered/i.test(message)) return "Un compte existe déjà avec cette adresse. Choisissez « J’ai déjà un compte ».";
    if (/password/i.test(message) && /characters|least/i.test(message)) return "Choisissez un mot de passe d’au moins 8 caractères.";
    return message || "La connexion a échoué. Réessayez dans un instant.";
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setSuccess("");
    setSubmitting(true);
    try {
      if (mode === "signup") {
        const result = await signUp(email, password, displayName);
        if (result === "confirmation-required") {
          setSuccess("Compte créé. Consultez votre messagerie pour confirmer votre adresse, puis connectez-vous.");
          setMode("login");
          setPassword("");
        }
      } else {
        await signIn(email, password);
      }
    } catch (caught) {
      setError(friendlyError(caught));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-hero">
        <div className="login-brand"><span className="brand-mark hero-mark">M</span><strong>Mijoté</strong></div>
        <p className="eyebrow">Notre carnet familial</p>
        <h1>Les recettes de votre famille, au bon endroit.</h1>
        <p className="login-intro">Un espace simple et privé pour cuisiner, transmettre et partager avec les bonnes personnes.</p>
        <div className="login-reassurance">
          <span>🔒 Privé</span><span>👪 Réservé aux invités</span><span>📱 Simple sur téléphone</span>
        </div>
      </section>
      <section className="login-panel">
        <div>
          <p className="login-step-label">{mode === "login" ? "Vous avez déjà un compte" : "C’est votre première visite"}</p>
          <h2>{mode === "login" ? "Ouvrir mon carnet" : "Créer mon compte"}</h2>
          <p className="muted">
            {mode === "login"
              ? "Saisissez l’adresse et le mot de passe choisis lors de votre inscription."
              : "Utilisez exactement l’adresse que la personne qui vous invite a autorisée."}
          </p>
        </div>
        {!isDemo && (
          <div className="login-mode-switch" role="tablist" aria-label="Choisir le parcours">
            <button type="button" className={mode === "login" ? "active" : ""} onClick={() => { setMode("login"); setError(""); setSuccess(""); }}>
              J’ai déjà un compte
            </button>
            <button type="button" className={mode === "signup" ? "active" : ""} onClick={() => { setMode("signup"); setError(""); setSuccess(""); }}>
              J’ai reçu une invitation
            </button>
          </div>
        )}
        {mode === "signup" && (
          <ol className="signup-steps">
            <li className="done"><span>1</span><div><strong>Adresse autorisée</strong><small>Votre proche vous a ajouté.</small></div></li>
            <li className="current"><span>2</span><div><strong>Créer le compte</strong><small>Choisissez votre nom et votre mot de passe.</small></div></li>
            <li><span>3</span><div><strong>Ouvrir Mijoté</strong><small>Vous arriverez directement sur les recettes.</small></div></li>
          </ol>
        )}
        {isDemo && (
          <div className="demo-banner">
            <span>Mode démonstration</span>
            <p>Les données restent uniquement dans ce navigateur.</p>
          </div>
        )}
        <form onSubmit={submit} className="stack">
          {mode === "signup" && (
            <label>
              Votre prénom ou nom
              <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} placeholder="Ex. Élisabeth" autoComplete="name" required />
            </label>
          )}
          <label>
            Adresse électronique
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="votre.adresse@exemple.fr"
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              required
            />
          </label>
          <label>
            Mot de passe
            <span className="password-field">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                minLength={8}
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                placeholder={mode === "signup" ? "8 caractères minimum" : "Votre mot de passe"}
                required
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}>
                {showPassword ? "Masquer" : "Afficher"}
              </button>
            </span>
            {mode === "signup" && <small className={password.length >= 8 ? "requirement valid" : "requirement"}>✓ Au moins 8 caractères</small>}
          </label>
          {success && <p className="success-text" role="status">{success}</p>}
          {error && <p className="form-error" role="alert">{error}</p>}
          <button className="primary-button full" type="submit" disabled={submitting}>
            {submitting ? "Un instant…" : mode === "login" ? "Me connecter" : "Créer mon compte et continuer"}
          </button>
        </form>
        {!isDemo && mode === "login" && (
          <button className="text-button login-help-toggle" type="button" onClick={() => setHelpOpen(!helpOpen)}>
            Mot de passe oublié ou besoin d’aide&nbsp;?
          </button>
        )}
        {helpOpen && (
          <div className="login-help">
            <strong>Réinitialiser mon mot de passe</strong>
            <p>Saisissez votre adresse ci-dessus, puis touchez le bouton. Vous recevrez un lien par courriel.</p>
            <button
              className="secondary-button full"
              type="button"
              disabled={!email || submitting}
              onClick={async () => {
                setError("");
                setSuccess("");
                setSubmitting(true);
                try {
                  await sendPasswordReset(email);
                  setSuccess("Lien envoyé. Consultez votre messagerie, y compris les courriers indésirables.");
                } catch (caught) {
                  setError(friendlyError(caught));
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              M’envoyer un lien
            </button>
          </div>
        )}
        <p className="privacy-note">🔒 Seules les personnes invitées peuvent entrer dans Mijoté.</p>
      </section>
    </main>
  );
}

function RecoveryPasswordPage() {
  const { updatePassword, exitRecovery } = useAuth();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  return (
    <main className="recovery-page">
      <section>
        <span className="brand-mark large">M</span>
        <p className="eyebrow">Sécuriser mon compte</p>
        <h1>Choisir un nouveau mot de passe</h1>
        <p>Utilisez au moins 8 caractères, puis conservez-le dans un endroit sûr.</p>
        <form
          className="stack"
          onSubmit={async (event) => {
            event.preventDefault();
            if (password.length < 8) return setError("Le mot de passe doit contenir au moins 8 caractères.");
            if (password !== confirmation) return setError("Les deux mots de passe ne sont pas identiques.");
            setSaving(true);
            setError("");
            try {
              await updatePassword(password);
            } catch (caught) {
              setError(caught instanceof Error ? caught.message : "La modification a échoué.");
            } finally {
              setSaving(false);
            }
          }}
        >
          <label>Nouveau mot de passe<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={8} required /></label>
          <label>Confirmer le mot de passe<input type="password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} minLength={8} required /></label>
          {error && <p className="form-error">{error}</p>}
          <button className="primary-button full" disabled={saving}>{saving ? "Enregistrement…" : "Enregistrer et ouvrir Mijoté"}</button>
          <button type="button" className="text-button" onClick={exitRecovery}>Annuler</button>
        </form>
      </section>
    </main>
  );
}

function RecipeCard({ recipe, compact = false }: { recipe: Recipe; compact?: boolean }) {
  return (
    <article className={`recipe-card ${compact ? "compact" : ""}`}>
      <button className="card-click" onClick={() => navigate(`recipe/${recipe.id}`)} aria-label={`Voir ${recipe.title}`}>
        <div className="recipe-photo">
          <RecipeVisual recipe={recipe} />
          <span className={`visibility-pill ${recipe.visibility}`}>
            {recipe.visibility === "shared" ? "Toute la famille" : recipe.sharedGroups.length && recipe.sharedWith.length ? "Partage ciblé" : recipe.sharedGroups.length ? "Cercle" : recipe.sharedWith.length ? "Partage direct" : "Privée"}
          </span>
        </div>
        <div className="recipe-card-body">
          <div className="card-meta">
            <span>{recipe.categories[0] ?? "Recette"}</span>
            <span>·</span>
            <span>{totalMinutes(recipe)} min</span>
          </div>
          <h3>{recipe.title}</h3>
          <p>{recipe.description}</p>
          <div className="author-line">
            <span className="mini-avatar">{initials(recipe.author.displayName)}</span>
            <span>{recipe.author.displayName}</span>
            {recipe.isFavorite && <span className="favorite-mark" aria-label="Dans les favoris">♥</span>}
          </div>
        </div>
      </button>
    </article>
  );
}

const initialFilters: RecipeFilters = {
  query: "",
  author: "Tous",
  category: "Toutes",
  maxDuration: null,
  visibility: "all",
};

function Dashboard() {
  const { user, isDemo } = useAuth();
  const { recipes, loading, error } = useRecipes();
  const { groups } = useGroups();
  const [filters, setFilters] = useState(initialFilters);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [installHelp, setInstallHelp] = useState(false);

  const authors = useMemo(
    () => [...new Map(recipes.map((recipe) => [recipe.author.id, recipe.author])).values()],
    [recipes],
  );
  const categories = useMemo(() => [...new Set(recipes.flatMap((recipe) => recipe.categories))].sort(), [recipes]);
  const filtered = filterRecipes(recipes, filters);
  const isFiltering =
    filters.query || filters.author !== "Tous" || filters.category !== "Toutes" || filters.maxDuration || filters.visibility !== "all";
  const recent = recipes.filter((recipe) => recipe.author.id !== user?.id).slice(0, 4);
  const mine = recipes.filter((recipe) => recipe.author.id === user?.id);
  const favorites = recipes.filter((recipe) => recipe.isFavorite);

  if (loading) return <LoadingScreen />;

  return (
    <>
      <AppHeader />
      <main className="page dashboard">
        {isDemo && <div className="demo-ribbon">Démonstration locale · aucune donnée réelle</div>}
        {!navigator.onLine && <div className="offline-banner">Hors connexion — les recettes en ligne ne sont pas disponibles.</div>}
        <section className="welcome">
          <div>
            <p className="eyebrow">Bonjour {user?.displayName?.split(" ")[0]} 👋</p>
            <h1>Votre carnet de famille</h1>
            <p>Retrouvez rapidement une recette ou ajoutez celle que vous venez de cuisiner.</p>
          </div>
          <button className="secondary-button install-button" onClick={() => setInstallHelp(!installHelp)}>
            Installer l’app
          </button>
        </section>
        {installHelp && (
          <div className="install-tip">
            <strong>Sur iPhone</strong>
            <p>Dans Safari, touchez Partager puis « Sur l’écran d’accueil ».</p>
          </div>
        )}
        <section className="dashboard-actions" aria-label="Actions rapides">
          <button className="primary-action-card" onClick={() => navigate("edit")}>
            <span>＋</span><div><strong>Ajouter une recette</strong><small>Le guide vous accompagne étape par étape</small></div>
          </button>
          <button className="action-card" onClick={() => navigate("groups")}>
            <span>◉</span><div><strong>Mes cercles</strong><small>{groups.length ? `${groups.length} espace${groups.length > 1 ? "s" : ""} privé${groups.length > 1 ? "s" : ""}` : "Partager avec les bonnes personnes"}</small></div>
          </button>
        </section>
        <section className="dashboard-summary" aria-label="Résumé du carnet">
          <button onClick={() => navigate("my-recipes")}><strong>{mine.length}</strong><span>Mes recettes</span></button>
          <div><strong>{favorites.length}</strong><span>Favoris</span></div>
          <div><strong>{groups.length}</strong><span>Cercles</span></div>
        </section>
        <section className="search-zone" aria-label="Rechercher et filtrer">
          <div className="search-input">
            <span>⌕</span>
            <input
              type="search"
              placeholder="Une recette, un ingrédient…"
              value={filters.query}
              onChange={(event) => setFilters({ ...filters, query: event.target.value })}
            />
            <button onClick={() => setFiltersOpen(!filtersOpen)} aria-label="Afficher les filtres">
              Filtres
            </button>
          </div>
          {filtersOpen && (
            <div className="filters-panel">
              <label>
                Auteur
                <select value={filters.author} onChange={(event) => setFilters({ ...filters, author: event.target.value })}>
                  <option>Tous</option>
                  {authors.map((author) => (
                    <option key={author.id} value={author.id}>
                      {author.displayName}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Catégorie
                <select value={filters.category} onChange={(event) => setFilters({ ...filters, category: event.target.value })}>
                  <option>Toutes</option>
                  {categories.map((category) => <option key={category}>{category}</option>)}
                </select>
              </label>
              <label>
                Durée
                <select
                  value={filters.maxDuration ?? ""}
                  onChange={(event) => setFilters({ ...filters, maxDuration: event.target.value ? Number(event.target.value) : null })}
                >
                  <option value="">Toutes</option>
                  <option value="15">15 min max.</option>
                  <option value="30">30 min max.</option>
                  <option value="60">1 h max.</option>
                </select>
              </label>
              <label>
                Visibilité
                <select
                  value={filters.visibility}
                  onChange={(event) => setFilters({ ...filters, visibility: event.target.value as RecipeFilters["visibility"] })}
                >
                  <option value="all">Toutes</option>
                  <option value="shared">Partagées</option>
                  <option value="private">Privées</option>
                </select>
              </label>
              <button className="text-button" onClick={() => setFilters(initialFilters)}>Effacer les filtres</button>
            </div>
          )}
        </section>
        {error && <div className="error-panel">Impossible de charger les recettes. {error}</div>}
        {isFiltering ? (
          <RecipeSection title={`${filtered.length} résultat${filtered.length > 1 ? "s" : ""}`} recipes={filtered} empty="Aucune recette ne correspond à ces critères." tone="search" compact />
        ) : (
          <>
            <RecipeSection title="À découvrir" subtitle="Les dernières recettes partagées avec vous" recipes={recent} tone="discover" compact />
            <RecipeSection title="Mon carnet" subtitle={`${mine.length} recette${mine.length > 1 ? "s" : ""} créée${mine.length > 1 ? "s" : ""} par vous`} recipes={mine} empty="Votre carnet attend sa première recette." tone="mine" compact />
            <RecipeSection title="Mes favorites" subtitle="Celles que vous voulez retrouver en un geste" recipes={favorites} empty="Touchez le cœur d’une recette pour la retrouver ici." tone="favorites" compact />
          </>
        )}
        <button className="floating-create" onClick={() => navigate("edit")} aria-label="Créer une recette">
          <span>＋</span> Nouvelle recette
        </button>
      </main>
      <BottomNav current="home" />
    </>
  );
}

function MyRecipesPage() {
  const { user } = useAuth();
  const { recipes, loading } = useRecipes();
  const [query, setQuery] = useState("");
  const mine = recipes.filter((recipe) => recipe.author.id === user?.id);
  const normalized = query.trim().toLocaleLowerCase("fr");
  const filtered = normalized
    ? mine.filter((recipe) =>
        [recipe.title, recipe.description, ...recipe.categories, ...recipe.ingredients.map((item) => item.name)]
          .join(" ")
          .toLocaleLowerCase("fr")
          .includes(normalized),
      )
    : mine;

  return (
    <>
      <AppHeader title="Mes recettes" back="" />
      <main className="page my-recipes-page">
        <section className="my-recipes-hero">
          <div>
            <p className="eyebrow">Votre carnet personnel</p>
            <h1>Les recettes que vous avez créées</h1>
            <p>Retrouvez ici uniquement vos propres recettes, qu’elles soient privées ou partagées.</p>
          </div>
          <button className="primary-button" onClick={() => navigate("edit")}>＋ Nouvelle recette</button>
        </section>
        <div className="my-recipes-search">
          <span aria-hidden="true">⌕</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Rechercher dans mes recettes…"
            aria-label="Rechercher dans mes recettes"
          />
        </div>
        {loading ? <div className="admin-loading">Chargement de votre carnet…</div> : (
          <RecipeSection
            title={`${filtered.length} recette${filtered.length > 1 ? "s" : ""}`}
            subtitle={query ? "Résultats dans votre carnet" : "Créées par vous"}
            recipes={filtered}
            empty={query ? "Aucune de vos recettes ne correspond à cette recherche." : "Votre carnet attend sa première recette."}
            tone="mine"
          />
        )}
      </main>
      <BottomNav current="recipes" />
    </>
  );
}

function RecipeSection({
  title,
  subtitle,
  recipes,
  empty,
  compact,
  tone = "default",
}: {
  title: string;
  subtitle?: string;
  recipes: Recipe[];
  empty?: string;
  compact?: boolean;
  tone?: "default" | "discover" | "mine" | "favorites" | "search";
}) {
  return (
    <section className={`recipe-section tone-${tone}`}>
      <div className="section-heading">
        <div>
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {recipes.length > 2 && <span className="section-count">{recipes.length}</span>}
      </div>
      {recipes.length ? (
        <div className={`recipe-grid ${compact ? "compact-grid" : ""}`}>
          {recipes.map((recipe) => <RecipeCard key={recipe.id} recipe={recipe} compact={compact} />)}
        </div>
      ) : (
        <div className="empty-state">
          <span>🍲</span>
          <p>{empty ?? "Rien à afficher pour le moment."}</p>
        </div>
      )}
    </section>
  );
}

function QuickShareModal({
  recipe,
  onClose,
  onSaved,
}: {
  recipe: Recipe;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { user } = useAuth();
  const { groups, familyMembers } = useGroups();
  const { updateRecipeShares } = useRecipes();
  const [groupIds, setGroupIds] = useState(recipe.sharedGroups.map((group) => group.id));
  const [userIds, setUserIds] = useState(recipe.sharedWith.map((profile) => profile.id));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const people = familyMembers.filter((member) => member.id !== user?.id);

  function toggle(values: string[], id: string) {
    return values.includes(id) ? values.filter((value) => value !== id) : [...values, id];
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="quick-share-modal" role="dialog" aria-modal="true" aria-labelledby="quick-share-title">
        <div className="modal-header">
          <div>
            <p className="eyebrow">Partage réservé à l’auteur</p>
            <h2 id="quick-share-title">Partager cette recette</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Fermer">×</button>
        </div>
        <div className="quick-share-note">
          <span aria-hidden="true">🔐</span>
          <p>
            Son statut principal reste <strong>{recipe.visibility === "shared" ? "visible par tout Mijoté" : "privé"}</strong>.
            Vous ajoutez simplement des destinataires précis.
          </p>
        </div>
        <section className="quick-share-section">
          <div><h3>Personnes</h3><span>{userIds.length} sélectionnée{userIds.length > 1 ? "s" : ""}</span></div>
          {people.length ? (
            <div className="quick-share-list">
              {people.map((member) => {
                const checked = userIds.includes(member.id);
                return (
                  <label className={checked ? "selected" : ""} key={member.id}>
                    <input type="checkbox" checked={checked} onChange={() => setUserIds(toggle(userIds, member.id))} />
                    <span className="mini-avatar">{initials(member.displayName)}</span>
                    <span><strong>{member.displayName}</strong><small>{checked ? "Peut voir cette recette" : "Partager directement"}</small></span>
                    <span className="audience-check">{checked ? "✓" : ""}</span>
                  </label>
                );
              })}
            </div>
          ) : <p className="empty-inline">Aucun autre membre n’est encore inscrit.</p>}
        </section>
        <section className="quick-share-section">
          <div><h3>Cercles</h3><span>{groupIds.length} sélectionné{groupIds.length > 1 ? "s" : ""}</span></div>
          {groups.length ? (
            <div className="quick-share-list">
              {groups.map((group) => {
                const checked = groupIds.includes(group.id);
                return (
                  <label className={checked ? "selected" : ""} key={group.id}>
                    <input type="checkbox" checked={checked} onChange={() => setGroupIds(toggle(groupIds, group.id))} />
                    <span className="mini-avatar">◉</span>
                    <span><strong>{group.name}</strong><small>{group.members.length} membre{group.members.length > 1 ? "s" : ""}</small></span>
                    <span className="audience-check">{checked ? "✓" : ""}</span>
                  </label>
                );
              })}
            </div>
          ) : <p className="empty-inline">Vous n’appartenez encore à aucun cercle.</p>}
        </section>
        {error && <p className="form-error" role="alert">{error}</p>}
        <div className="quick-share-footer">
          <button className="text-button" type="button" onClick={onClose}>Annuler</button>
          <button
            className="primary-button"
            type="button"
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              setError("");
              try {
                await updateRecipeShares(recipe.id, groupIds, userIds);
                onSaved();
              } catch (caught) {
                setError(caught instanceof Error ? caught.message : "Le partage a échoué.");
              } finally {
                setSaving(false);
              }
            }}
          >
            {saving ? "Enregistrement…" : "Enregistrer les destinataires"}
          </button>
        </div>
      </section>
    </div>
  );
}

function RecipeDetail({ recipe }: { recipe: Recipe }) {
  const { user } = useAuth();
  const { toggleFavorite, deleteRecipe } = useRecipes();
  const [toast, setToast] = useState("");
  const [quickShareOpen, setQuickShareOpen] = useState(false);
  const isAuthor = recipe.author.id === user?.id;

  async function remove() {
    if (!window.confirm(`Supprimer définitivement « ${recipe.title} » ?`)) return;
    await deleteRecipe(recipe.id);
    navigate();
  }

  return (
    <>
      <AppHeader back="" />
      <main className="detail-page">
        <div className="detail-hero">
          <RecipeVisual recipe={recipe} detail />
          <div className="detail-actions">
            <button
              className={`circle-action ${recipe.isFavorite ? "active" : ""}`}
              onClick={async () => {
                await toggleFavorite(recipe.id);
                setToast(recipe.isFavorite ? "Retirée des favoris" : "Ajoutée aux favoris");
              }}
              aria-label={recipe.isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
            >
              ♥
            </button>
          </div>
        </div>
        <div className="detail-content">
          <div className="title-block">
            <div className="tag-row">
              {recipe.categories.map((category) => <span className="tag" key={category}>{category}</span>)}
              <span className={`visibility-label ${recipe.visibility}`}>
                {recipeVisibilityTag(recipe)}
              </span>
            </div>
            <h1>{recipe.title}</h1>
            <p>{recipe.description}</p>
            <div className="author-line generous">
              <span className="mini-avatar">{initials(recipe.author.displayName)}</span>
              <span>Recette de <strong>{recipe.author.displayName}</strong></span>
            </div>
            <div className="audience-summary">
              <span aria-hidden="true">👁</span>
              <div><small>Visible par</small><strong>{recipeAudienceLabel(recipe)}</strong></div>
            </div>
            <div className="detail-primary-actions">
              <button className="cook-button" onClick={() => navigate(`cook/${recipe.id}`)}>▶ Démarrer le mode cuisine</button>
              {isAuthor && (
                <button className="quick-share-button" onClick={() => setQuickShareOpen(true)}>
                  <span aria-hidden="true">↗</span>
                  <span><strong>Partager</strong><small>Choisir une personne ou un cercle</small></span>
                </button>
              )}
            </div>
          </div>
          <div className="stats">
            <div><span>Préparation</span><strong>{recipe.prepMinutes} min</strong></div>
            <div><span>Cuisson</span><strong>{recipe.cookMinutes} min</strong></div>
            <div><span>Portions</span><strong>{recipe.servings}</strong></div>
            {recipe.difficulty && <div><span>Difficulté</span><strong className="capitalize">{recipe.difficulty}</strong></div>}
          </div>
          <div className="detail-columns">
            <section>
              <h2>Ingrédients</h2>
              <ul className="ingredient-list">
                {recipe.ingredients.map((item) => (
                  <li key={item.id}>
                    <span className="ingredient-dot" />
                    <span><strong>{[item.quantity, item.unit].filter(Boolean).join(" ")}</strong> {item.name} {item.note && <small>— {item.note}</small>}</span>
                  </li>
                ))}
              </ul>
            </section>
            <section>
              <h2>Préparation</h2>
              <ol className="step-list">
                {recipe.steps.map((item, index) => {
                  const asset = findIllustration(item.illustrationId);
                  return (
                    <li key={item.id}>
                      <span className="step-number">{index + 1}</span>
                      <div className="step-copy">
                        <p>{item.instruction}</p>
                        {item.durationMinutes && <span className="duration-chip">◷ {item.durationMinutes} min</span>}
                      </div>
                      {asset && (
                        <div className="step-illustration">
                          <img src={asset.url} alt={asset.alt} />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ol>
            </section>
          </div>
          {isAuthor && (
            <div className="owner-actions">
              <button className="secondary-button" onClick={() => navigate(`edit/${recipe.id}`)}>Modifier la recette</button>
              <button className="danger-button" onClick={remove}>Supprimer</button>
            </div>
          )}
        </div>
      </main>
      {toast && <Toast message={toast} onClose={() => setToast("")} />}
      {quickShareOpen && (
        <QuickShareModal
          recipe={recipe}
          onClose={() => setQuickShareOpen(false)}
          onSaved={() => {
            setQuickShareOpen(false);
            setToast("Destinataires mis à jour");
          }}
        />
      )}
    </>
  );
}

function IllustrationPicker({
  selected,
  onSelect,
  onClose,
}: {
  selected?: string;
  onSelect: (id: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("Toutes");
  const assets = filterIllustrations(query, category);
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="asset-modal" role="dialog" aria-modal="true" aria-labelledby="asset-title">
        <div className="modal-header">
          <div><p className="eyebrow">Bibliothèque commune</p><h2 id="asset-title">Choisir une illustration</h2></div>
          <button className="icon-button" onClick={onClose} aria-label="Fermer">×</button>
        </div>
        <input type="search" placeholder="Rechercher une action…" value={query} onChange={(event) => setQuery(event.target.value)} autoFocus />
        <div className="category-tabs">
          {["Toutes", ...illustrationCategories].map((item) => (
            <button key={item} className={category === item ? "active" : ""} onClick={() => setCategory(item)}>{item}</button>
          ))}
        </div>
        <div className="asset-grid">
          {assets.map((asset) => (
            <button
              key={asset.id}
              className={selected === asset.id ? "selected" : ""}
              onClick={() => {
                onSelect(asset.id);
                onClose();
              }}
            >
              <img src={asset.url} alt={asset.alt} />
              <strong>{asset.name}</strong>
              <small>{asset.category}</small>
            </button>
          ))}
        </div>
        {!assets.length && <div className="empty-state"><span>🔎</span><p>Aucune illustration trouvée.</p></div>}
      </section>
    </div>
  );
}

const DRAFT_KEY = "mijote-current-draft-v1";

function RecipeForm({ existing }: { existing?: Recipe }) {
  const { user } = useAuth();
  const { saveRecipe } = useRecipes();
  const { categories, loading: categoriesLoading } = useCategories();
  const { groups, familyMembers } = useGroups();
  const [draft, setDraft] = useState<RecipeDraft>(() => {
    if (existing) {
      return {
        title: existing.title,
        description: existing.description,
        categories: existing.categories,
        servings: existing.servings,
        prepMinutes: existing.prepMinutes,
        cookMinutes: existing.cookMinutes,
        difficulty: existing.difficulty,
        photoUrl: existing.photoUrl,
        visibility: existing.visibility,
        sharedGroupIds: existing.sharedGroups.map((group) => group.id),
        sharedUserIds: existing.sharedWith.map((profile) => profile.id),
        ingredients: existing.ingredients,
        steps: existing.steps,
      };
    }
    const saved = localStorage.getItem(DRAFT_KEY);
    if (!saved) return emptyDraft();
    const parsed = JSON.parse(saved) as RecipeDraft;
    return { ...parsed, sharedGroupIds: parsed.sharedGroupIds ?? [], sharedUserIds: parsed.sharedUserIds ?? [], photoUrl: undefined };
  });
  const [formStep, setFormStep] = useState(0);
  const [audienceMode, setAudienceMode] = useState<"private" | "everyone" | "groups" | "people">(
    existing?.visibility === "shared" ? "everyone" : existing?.sharedGroups.length ? "groups" : existing?.sharedWith.length ? "people" : "private",
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pickerStep, setPickerStep] = useState<number | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState(existing?.photoUrl);
  const [photoError, setPhotoError] = useState("");
  const [saving, setSaving] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);

  useEffect(() => {
    if (existing) return;
    const timeout = window.setTimeout(() => {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      setDraftSaved(true);
      window.setTimeout(() => setDraftSaved(false), 1800);
    }, 600);
    return () => window.clearTimeout(timeout);
  }, [draft, existing]);

  function updateIngredient(index: number, patch: Partial<Ingredient>) {
    setDraft({ ...draft, ingredients: draft.ingredients.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item) });
  }

  function updateStep(index: number, patch: Partial<RecipeStep>) {
    setDraft({ ...draft, steps: draft.steps.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item) });
  }

  function goNext() {
    if (formStep === 3 && audienceMode === "groups" && !draft.sharedGroupIds.length) {
      setErrors({ audience: "Choisissez au moins un cercle, ou sélectionnez un autre type de partage." });
      return;
    }
    if (formStep === 3 && audienceMode === "people" && !draft.sharedUserIds.length) {
      setErrors({ audience: "Choisissez la personne qui pourra voir cette recette." });
      return;
    }
    const nextErrors = validateRecipe(draft);
    const relevantKeys = formStep === 0 ? ["title", "servings", "time"] : formStep === 1 ? ["ingredients"] : formStep === 2 ? ["steps"] : [];
    const relevant = Object.fromEntries(Object.entries(nextErrors).filter(([key]) => relevantKeys.includes(key)));
    if (Object.keys(relevant).length) {
      setErrors(relevant);
      window.setTimeout(() => document.querySelector(".field-error")?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
      return;
    }
    setErrors({});
    setFormStep((current) => Math.min(4, current + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function chooseAudience(mode: typeof audienceMode) {
    setAudienceMode(mode);
    setDraft((current) => ({
      ...current,
      visibility: mode === "everyone" ? "shared" : "private",
      sharedGroupIds: mode === "groups" ? current.sharedGroupIds : [],
      sharedUserIds: mode === "people" ? current.sharedUserIds : [],
    }));
  }

  async function choosePhoto(file?: File) {
    if (!file) return;
    setPhotoError("");
    try {
      const compressed = await compressRecipePhoto(file);
      setPhotoFile(compressed);
      setPhotoPreview(URL.createObjectURL(compressed));
      setDraft((current) => ({ ...current, photoUrl: undefined }));
    } catch (caught) {
      setPhotoError(caught instanceof Error ? caught.message : "Photo non prise en charge.");
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const nextErrors = validateRecipe(draft);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      document.querySelector(".field-error")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSaving(true);
    try {
      const saved = await saveRecipe(draft, existing?.id, photoFile);
      localStorage.removeItem(DRAFT_KEY);
      navigate(`recipe/${saved.id}`);
    } catch (caught) {
      setErrors({ submit: caught instanceof Error ? caught.message : "L’enregistrement a échoué." });
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <AppHeader title={existing ? "Modifier" : "Nouvelle recette"} back={existing ? `recipe/${existing.id}` : ""} />
      <main className="form-page page">
        <div className="form-intro">
          <div><p className="eyebrow">{existing ? "Modifier sans rien oublier" : "Un petit pas à la fois"}</p><h1>{existing ? "Ajuster la recette" : "Ajouter une recette"}</h1></div>
          <span className={`save-indicator ${draftSaved ? "visible" : ""}`}>✓ Brouillon enregistré sur cet appareil</span>
        </div>
        <div className="form-wizard" aria-label={`Étape ${formStep + 1} sur 5`}>
          {["Informations", "Ingrédients", "Préparation", "Partage", "Vérification"].map((label, index) => (
            <button
              type="button"
              key={label}
              className={index === formStep ? "current" : index < formStep ? "done" : ""}
              onClick={() => index < formStep && setFormStep(index)}
              disabled={index > formStep}
            >
              <span>{index < formStep ? "✓" : index + 1}</span><small>{label}</small>
            </button>
          ))}
          <div className="wizard-track"><span style={{ width: `${((formStep + 1) / 5) * 100}%` }} /></div>
        </div>
        <form onSubmit={submit}>
          <section className="form-section" hidden={formStep !== 0}>
            <div className="section-number">1</div>
            <div className="form-section-content">
              <div className="form-section-title"><p>Étape 1 sur 5</p><h2>Commençons par l’essentiel</h2><span>Donnez quelques repères. Vous pourrez tout modifier plus tard.</span></div>
              <label>
                Titre de la recette *
                <input
                  value={draft.title}
                  onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                  placeholder="Ex. Gratin de courgettes de Papa"
                  className={errors.title ? "invalid" : ""}
                />
                {errors.title && <span className="field-error">{errors.title}</span>}
              </label>
              <label>
                Courte description
                <textarea
                  value={draft.description}
                  onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                  placeholder="L’histoire ou le petit plus de cette recette…"
                  rows={3}
                  maxLength={400}
                />
              </label>
              <div className="recipe-category-field">
                <div>
                  <strong>Catégories</strong>
                  <small>Choisissez une ou plusieurs catégories</small>
                </div>
                {categoriesLoading ? (
                  <span className="muted">Chargement des catégories…</span>
                ) : (
                  <div className="recipe-category-options">
                    {categories.map((category) => {
                      const selected = draft.categories.includes(category.name);
                      return (
                        <button
                          type="button"
                          key={category.id}
                          className={selected ? "selected" : ""}
                          aria-pressed={selected}
                          onClick={() => setDraft({
                            ...draft,
                            categories: selected
                              ? draft.categories.filter((name) => name !== category.name)
                              : [...draft.categories, category.name],
                          })}
                        >
                          {category.name}
                        </button>
                      );
                    })}
                  </div>
                )}
                <button type="button" className="text-button category-manage-link" onClick={() => navigate("categories")}>
                  ＋ Créer une nouvelle catégorie depuis mon profil
                </button>
              </div>
              <div className="photo-field">
                <span>Photo du résultat</span>
                <input
                  id="recipe-photo"
                  className="visually-hidden"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                  onChange={(event) => void choosePhoto(event.target.files?.[0])}
                />
                {photoPreview ? (
                  <div className="photo-preview">
                    <img src={photoPreview} alt="Aperçu de la recette" />
                    <div>
                      <label className="secondary-button photo-replace-button" htmlFor="recipe-photo">Remplacer</label>
                      <button
                        type="button"
                        className="text-button danger-text"
                        onClick={() => {
                          setPhotoPreview(undefined);
                          setPhotoFile(null);
                          setDraft({ ...draft, photoUrl: undefined });
                        }}
                      >
                        Retirer
                      </button>
                    </div>
                  </div>
                ) : (
                  <label className="photo-drop" htmlFor="recipe-photo">
                    <span>📷</span><strong>Choisir une photo</strong><small>JPEG, PNG, WebP ou photo iPhone · 12 Mo maximum</small>
                  </label>
                )}
                {photoError && <span className="field-error">{photoError}</span>}
              </div>
            </div>
          </section>
          <section className="form-section form-section-inline" hidden={formStep !== 0}>
            <div className="section-number">⌚</div>
            <div className="form-section-content">
              <h2>Temps et portions</h2>
              <div className="field-grid">
                <label>Portions<input type="number" min="1" max="50" value={draft.servings} onChange={(event) => setDraft({ ...draft, servings: Number(event.target.value) })} /></label>
                <label>Préparation (min)<input type="number" min="0" value={draft.prepMinutes} onChange={(event) => setDraft({ ...draft, prepMinutes: Number(event.target.value) })} /></label>
                <label>Cuisson (min)<input type="number" min="0" value={draft.cookMinutes} onChange={(event) => setDraft({ ...draft, cookMinutes: Number(event.target.value) })} /></label>
                <label>
                  Difficulté
                  <select value={draft.difficulty ?? ""} onChange={(event) => setDraft({ ...draft, difficulty: (event.target.value || undefined) as RecipeDraft["difficulty"] })}>
                    <option value="">Non précisée</option><option value="facile">Facile</option><option value="moyen">Moyen</option><option value="difficile">Difficile</option>
                  </select>
                </label>
              </div>
              {errors.time && <span className="field-error">{errors.time}</span>}
            </div>
          </section>
          <section className="form-section" hidden={formStep !== 1}>
            <div className="section-number">2</div>
            <div className="form-section-content">
              <div className="form-section-title"><p>Étape 2 sur 5</p><h2>Ajoutez les ingrédients</h2><span>Commencez par le nom. La quantité, l’unité et la remarque sont facultatives.</span></div>
              <div className="sortable-list">
                {draft.ingredients.map((item, index) => (
                  <div className="ingredient-editor" key={item.id}>
                    <div className="reorder-controls">
                      <button type="button" onClick={() => setDraft({ ...draft, ingredients: reorder(draft.ingredients, index, -1) })} disabled={index === 0} aria-label="Monter">↑</button>
                      <button type="button" onClick={() => setDraft({ ...draft, ingredients: reorder(draft.ingredients, index, 1) })} disabled={index === draft.ingredients.length - 1} aria-label="Descendre">↓</button>
                    </div>
                    <div className="ingredient-fields">
                      <input value={item.name} onChange={(event) => updateIngredient(index, { name: event.target.value })} placeholder="Ingrédient" aria-label={`Nom de l’ingrédient ${index + 1}`} />
                      <div>
                        <input value={item.quantity ?? ""} onChange={(event) => updateIngredient(index, { quantity: event.target.value })} placeholder="Qté" aria-label="Quantité" />
                        <input value={item.unit ?? ""} onChange={(event) => updateIngredient(index, { unit: event.target.value })} placeholder="Unité" aria-label="Unité" />
                      </div>
                      <input value={item.note ?? ""} onChange={(event) => updateIngredient(index, { note: event.target.value })} placeholder="Remarque facultative" aria-label="Remarque" />
                    </div>
                    <button type="button" className="remove-row" onClick={() => setDraft({ ...draft, ingredients: draft.ingredients.filter((_, itemIndex) => itemIndex !== index).map((entry, position) => ({ ...entry, position })) })} aria-label="Supprimer cet ingrédient">×</button>
                  </div>
                ))}
              </div>
              {errors.ingredients && <span className="field-error">{errors.ingredients}</span>}
              <button type="button" className="add-row-button" onClick={() => setDraft({ ...draft, ingredients: [...draft.ingredients, { id: createClientId(), name: "", position: draft.ingredients.length }] })}>＋ Ajouter l’ingrédient suivant</button>
            </div>
          </section>
          <section className="form-section" hidden={formStep !== 2}>
            <div className="section-number">3</div>
            <div className="form-section-content">
              <div className="form-section-title"><p>Étape 3 sur 5</p><h2>Expliquez la préparation</h2><span>Une action simple par étape aide beaucoup la personne qui cuisinera.</span></div>
              <div className="sortable-list">
                {draft.steps.map((item, index) => {
                  const asset = findIllustration(item.illustrationId);
                  return (
                    <div className="step-editor" key={item.id}>
                      <div className="step-editor-head">
                        <span>Étape {index + 1}</span>
                        <div className="row-actions">
                          <button type="button" onClick={() => setDraft({ ...draft, steps: reorder(draft.steps, index, -1) })} disabled={index === 0} aria-label="Monter l’étape">↑</button>
                          <button type="button" onClick={() => setDraft({ ...draft, steps: reorder(draft.steps, index, 1) })} disabled={index === draft.steps.length - 1} aria-label="Descendre l’étape">↓</button>
                          <button type="button" className="remove-row" onClick={() => setDraft({ ...draft, steps: draft.steps.filter((_, itemIndex) => itemIndex !== index).map((entry, position) => ({ ...entry, position })) })} aria-label="Supprimer l’étape">×</button>
                        </div>
                      </div>
                      <label className="step-instruction-label">
                        Instruction
                        <textarea value={item.instruction} onChange={(event) => updateStep(index, { instruction: event.target.value })} placeholder="Ex. Mélanger la farine et le sucre dans un grand bol." rows={4} />
                      </label>
                      <div className="step-options">
                        <label>Durée <input type="number" min="0" value={item.durationMinutes ?? ""} onChange={(event) => updateStep(index, { durationMinutes: event.target.value ? Number(event.target.value) : undefined })} placeholder="min" /></label>
                        <button type="button" className="illustration-choice" onClick={() => setPickerStep(index)}>
                          {asset ? <img src={asset.url} alt="" /> : <span>＋</span>}
                          <span>{asset?.name ?? "Choisir une illustration"}</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {errors.steps && <span className="field-error">{errors.steps}</span>}
              <button type="button" className="add-row-button" onClick={() => setDraft({ ...draft, steps: [...draft.steps, { id: createClientId(), instruction: "", position: draft.steps.length }] })}>＋ Ajouter l’étape suivante</button>
            </div>
          </section>
          <section className="form-section" hidden={formStep !== 3}>
            <div className="section-number">4</div>
            <div className="form-section-content">
              <div className="form-section-title"><p>Étape 4 sur 5</p><h2>Qui pourra voir cette recette&nbsp;?</h2><span>Ce choix est protégé dans la base de données et reste modifiable.</span></div>
              <div className="audience-options">
                {([
                  ["private", "🔒", "Seulement moi", "Personne d’autre ne la verra"],
                  ["everyone", "👪", "Tout Mijoté", "Tous les membres autorisés"],
                  ["groups", "◉", "Un ou plusieurs cercles", "Ex. Parents et sœurs"],
                  ["people", "↗", "Une personne précise", "Envoi privé direct"],
                ] as const).map(([mode, icon, title, description]) => (
                  <button type="button" className={audienceMode === mode ? "selected" : ""} key={mode} onClick={() => chooseAudience(mode)}>
                    <span>{icon}</span><span><strong>{title}</strong><small>{description}</small></span><span className="audience-check">{audienceMode === mode ? "✓" : ""}</span>
                  </button>
                ))}
              </div>
              {audienceMode === "groups" && (
                <div className="audience-picker">
                  <h3>Choisir les cercles</h3>
                  {groups.length ? groups.map((group) => {
                    const checked = draft.sharedGroupIds.includes(group.id);
                    return <label className={checked ? "selected" : ""} key={group.id}><input type="checkbox" checked={checked} onChange={() => setDraft({ ...draft, sharedGroupIds: checked ? draft.sharedGroupIds.filter((id) => id !== group.id) : [...draft.sharedGroupIds, group.id] })} /><span>◉</span><span><strong>{group.name}</strong><small>{group.members.length} membres</small></span></label>;
                  }) : <div className="empty-inline"><p>Vous n’avez encore aucun cercle.</p><button type="button" className="secondary-button" onClick={() => navigate("groups")}>Créer un cercle</button></div>}
                </div>
              )}
              {audienceMode === "people" && (
                <div className="audience-picker">
                  <h3>Choisir une personne</h3>
                  {familyMembers.filter((member) => member.id !== user?.id).length === 0 && (
                    <div className="empty-inline">
                      <p>Aucun autre membre n’est encore inscrit. L’administrateur peut d’abord inviter la personne.</p>
                    </div>
                  )}
                  {familyMembers.filter((member) => member.id !== user?.id).map((member) => {
                    const checked = draft.sharedUserIds.includes(member.id);
                    return <label className={checked ? "selected" : ""} key={member.id}><input type="radio" name="direct-recipient" checked={checked} onChange={() => setDraft({ ...draft, sharedUserIds: [member.id] })} /><span className="mini-avatar">{initials(member.displayName)}</span><span><strong>{member.displayName}</strong><small>{checked ? "Recevra cette recette" : "Partager directement"}</small></span></label>;
                  })}
                </div>
              )}
              {errors.audience && <span className="field-error">{errors.audience}</span>}
            </div>
          </section>
          <section className="form-section review-section" hidden={formStep !== 4}>
            <div className="section-number">5</div>
            <div className="form-section-content">
              <div className="form-section-title"><p>Dernière étape</p><h2>Vérifiez avant d’enregistrer</h2><span>Tout est prêt. Votre recette ne sera visible que par le public choisi.</span></div>
              <div className="recipe-review">
                <div><span>Titre</span><strong>{draft.title || "Sans titre"}</strong><button type="button" onClick={() => setFormStep(0)}>Modifier</button></div>
                <div><span>Contenu</span><strong>{draft.ingredients.length} ingrédient{draft.ingredients.length > 1 ? "s" : ""} · {draft.steps.length} étape{draft.steps.length > 1 ? "s" : ""}</strong><button type="button" onClick={() => setFormStep(1)}>Modifier</button></div>
                <div><span>Partage</span><strong>{audienceMode === "private" ? "Seulement moi" : audienceMode === "everyone" ? "Tout Mijoté" : audienceMode === "groups" ? `${draft.sharedGroupIds.length} cercle${draft.sharedGroupIds.length > 1 ? "s" : ""}` : `${draft.sharedUserIds.length} personne${draft.sharedUserIds.length > 1 ? "s" : ""}`}</strong><button type="button" onClick={() => setFormStep(3)}>Modifier</button></div>
              </div>
              <div className="review-reassurance">✓ Le brouillon est conservé sur cet appareil jusqu’à l’enregistrement.</div>
            </div>
          </section>
          {errors.submit && <div className="error-panel">{errors.submit}</div>}
          <div className="form-footer">
            {formStep === 0 ? (
              <button type="button" className="text-button" onClick={() => navigate(existing ? `recipe/${existing.id}` : "")}>Quitter</button>
            ) : (
              <button type="button" className="secondary-button" onClick={() => { setFormStep(formStep - 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}>← Retour</button>
            )}
            <span className="footer-step">Étape {formStep + 1}/5</span>
            {formStep < 4 ? (
              <button type="button" className="primary-button" onClick={goNext}>Continuer →</button>
            ) : (
              <button className="primary-button" type="submit" disabled={saving}>{saving ? "Enregistrement…" : existing ? "Enregistrer les modifications" : "Enregistrer la recette ✓"}</button>
            )}
          </div>
        </form>
      </main>
      {pickerStep !== null && (
        <IllustrationPicker
          selected={draft.steps[pickerStep]?.illustrationId}
          onSelect={(id) => updateStep(pickerStep, { illustrationId: id })}
          onClose={() => setPickerStep(null)}
        />
      )}
    </>
  );
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false,
  );

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return reduced;
}

function KitchenIllustration({ asset }: { asset: IllustrationAsset }) {
  const reducedMotion = useReducedMotion();
  const animated = KITCHEN_ILLUSTRATIONS_ANIMATED && !reducedMotion && asset.animatedUrl;
  return (
    <div className="kitchen-illustration">
      <img src={animated || asset.url} alt={asset.alt} />
    </div>
  );
}

function KitchenMode({ recipe }: { recipe: Recipe }) {
  const [index, setIndex] = useState(0);
  const [checkedIngredients, setCheckedIngredients] = useState<Set<string>>(new Set());
  const [doneSteps, setDoneSteps] = useState<Set<string>>(new Set());
  const step = recipe.steps[index]!;
  const asset = findIllustration(step.illustrationId);
  const finished = index === recipe.steps.length - 1;

  function toggle(setter: typeof setCheckedIngredients, current: Set<string>, id: string) {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setter(next);
  }

  return (
    <main className="kitchen-page">
      <header className="kitchen-header">
        <button className="icon-button" onClick={() => navigate(`recipe/${recipe.id}`)} aria-label="Quitter le mode cuisine">×</button>
        <div><span>Mode cuisine</span><strong>{recipe.title}</strong></div>
        <span>{index + 1}/{recipe.steps.length}</span>
      </header>
      <div className="progress-track"><span style={{ width: `${((index + 1) / recipe.steps.length) * 100}%` }} /></div>
      <div className="kitchen-layout">
        <aside>
          <h2>Ingrédients</h2>
          {recipe.ingredients.map((item) => (
            <label className={checkedIngredients.has(item.id) ? "checked" : ""} key={item.id}>
              <input type="checkbox" checked={checkedIngredients.has(item.id)} onChange={() => toggle(setCheckedIngredients, checkedIngredients, item.id)} />
              <span><strong>{[item.quantity, item.unit].filter(Boolean).join(" ")}</strong> {item.name}</span>
            </label>
          ))}
        </aside>
        <section className="kitchen-step">
          {asset && (
            <KitchenIllustration asset={asset} />
          )}
          <p className="eyebrow">Étape {index + 1}</p>
          <h1>{step.instruction}</h1>
          {step.durationMinutes && <div className="kitchen-timer">◷ Environ {step.durationMinutes} min</div>}
          <label className="step-done">
            <input type="checkbox" checked={doneSteps.has(step.id)} onChange={() => toggle(setDoneSteps, doneSteps, step.id)} />
            Étape terminée
          </label>
        </section>
      </div>
      <nav className="kitchen-nav">
        <button className="secondary-button" disabled={index === 0} onClick={() => setIndex(index - 1)}>← Précédent</button>
        {finished ? (
          <button className="primary-button" onClick={() => navigate(`recipe/${recipe.id}`)}>Terminer ✓</button>
        ) : (
          <button className="primary-button" onClick={() => setIndex(index + 1)}>Suivant →</button>
        )}
      </nav>
    </main>
  );
}

function ProfilePage() {
  const { user, signOut, updateProfile, isDemo, isAdmin } = useAuth();
  const [displayName, setDisplayName] = useState(user?.displayName ?? "");
  const [saved, setSaved] = useState(false);
  return (
    <>
      <AppHeader title="Mon profil" back="" />
      <main className="page profile-page">
        <div className="profile-card">
          <div className="profile-avatar">{initials(displayName || "M")}</div>
          <div><h1>{displayName || "Mon profil"}</h1><p>{user?.email}</p></div>
        </div>
        <section className="profile-form">
          <h2>Mes informations</h2>
          <label>Nom affiché<input value={displayName} onChange={(event) => setDisplayName(event.target.value)} /></label>
          <p className="muted">L’avatar personnalisé sera relié au stockage Supabase dans une prochaine itération.</p>
          <button
            className="primary-button"
            onClick={async () => {
              await updateProfile(displayName);
              setSaved(true);
            }}
          >
            Enregistrer
          </button>
          {saved && <p className="success-text">✓ Profil mis à jour</p>}
        </section>
        <section className="category-entry">
          <div>
            <span className="category-entry-icon" aria-hidden="true">▦</span>
            <div>
              <strong>Mes catégories</strong>
              <p>Retrouvez les catégories partagées ou proposez-en une nouvelle.</p>
            </div>
          </div>
          <button className="secondary-button" onClick={() => navigate("categories")}>Gérer les catégories</button>
        </section>
        <section className="group-entry">
          <div>
            <span className="group-entry-icon" aria-hidden="true">◉</span>
            <div>
              <strong>Mes cercles de partage</strong>
              <p>Séparez les recettes des parents, cousins, amis ou belle-famille.</p>
            </div>
          </div>
          <button className="secondary-button" onClick={() => navigate("groups")}>Ouvrir mes cercles</button>
        </section>
        {isAdmin && (
          <section className="admin-entry">
            <div>
              <span className="admin-entry-icon" aria-hidden="true">✉</span>
              <div>
                <strong>Inviter ma famille</strong>
                <p>Autorisez de nouvelles adresses et suivez les inscriptions.</p>
              </div>
            </div>
            <button className="primary-button" onClick={() => navigate("admin")}>Gérer les invitations</button>
          </section>
        )}
        <section className="account-panel">
          <div><strong>{isDemo ? "Démonstration locale" : "Compte familial"}</strong><p>Vos recettes privées ne sont visibles que par vous.</p></div>
          <button className="danger-button" onClick={() => void signOut()}>Se déconnecter</button>
        </section>
      </main>
      <BottomNav current="profile" />
    </>
  );
}

function CategoryPage() {
  const { user } = useAuth();
  const { categories, loading, error: loadError, addCategory } = useCategories();
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      await addCategory(name);
      setSuccess(`La catégorie « ${name.trim()} » est maintenant disponible pour toute la famille.`);
      setName("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Impossible de créer cette catégorie.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <AppHeader title="Catégories" back="profile" />
      <main className="page category-page">
        <section className="category-page-hero">
          <p className="eyebrow">Le rangement du carnet</p>
          <h1>Les catégories de la famille</h1>
          <p>Chaque membre peut proposer une catégorie. Les nouvelles utilisent d’abord l’illustration maison, puis peuvent recevoir leur propre couverture.</p>
        </section>

        <section className="category-create-card">
          <div>
            <h2>Créer une catégorie</h2>
            <p className="muted">Choisissez un nom court et utile à toute la famille.</p>
          </div>
          <form onSubmit={submit}>
            <label>
              Nom de la catégorie
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                minLength={2}
                maxLength={60}
                placeholder="Ex. Cuisine antillaise"
                required
              />
            </label>
            <button className="primary-button" type="submit" disabled={submitting}>
              {submitting ? "Création…" : "Ajouter à Mijoté"}
            </button>
          </form>
          {success && <p className="success-text" role="status">✓ {success}</p>}
          {(error || loadError) && <p className="form-error" role="alert">{error || loadError}</p>}
        </section>

        <section className="category-library">
          <div className="section-heading">
            <div><h2>Bibliothèque actuelle</h2><p>{categories.length} catégorie{categories.length > 1 ? "s" : ""}</p></div>
          </div>
          {loading ? (
            <div className="admin-loading">Chargement des catégories…</div>
          ) : (
            <div className="category-card-grid">
              {categories.map((category) => (
                <article className="category-card" key={category.id}>
                  <img src={categoryCoverUrl(category.coverKey)} alt="" />
                  <div>
                    <strong>{category.name}</strong>
                    <span>
                      {category.isFixed
                        ? "Catégorie Mijoté"
                        : category.createdBy === user?.id
                          ? "Créée par vous"
                          : "Créée par la famille"}
                    </span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>
    </>
  );
}

function MemberChoices({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const { user } = useAuth();
  const { familyMembers } = useGroups();
  const choices = familyMembers.filter((member) => member.id !== user?.id);
  if (!choices.length) return <p className="muted">Invitez d’abord un proche pour pouvoir l’ajouter à un cercle.</p>;
  return (
    <div className="member-choice-list">
      {choices.map((member) => {
        const checked = selected.includes(member.id);
        return (
          <label className={checked ? "selected" : ""} key={member.id}>
            <input
              type="checkbox"
              checked={checked}
              onChange={() => onChange(checked ? selected.filter((id) => id !== member.id) : [...selected, member.id])}
            />
            <span className="mini-avatar">{initials(member.displayName)}</span>
            <span><strong>{member.displayName}</strong><small>{checked ? "Sera membre du cercle" : "Ajouter au cercle"}</small></span>
          </label>
        );
      })}
    </div>
  );
}

function GroupsPage() {
  const { groups, loading, error: loadError, createGroup } = useGroups();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const id = await createGroup(name, description, memberIds);
      navigate(`groups/${id}`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Le cercle n’a pas pu être créé.");
      setSaving(false);
    }
  }

  return (
    <>
      <AppHeader title="Mes cercles" back="" />
      <main className="page groups-page">
        <section className="groups-hero">
          <div><p className="eyebrow">Partager sans tout mélanger</p><h1>Vos espaces privés</h1><p>Chaque cercle possède ses membres et ses recettes. Une personne ne voit que les cercles auxquels elle appartient.</p></div>
          <button className="primary-button" onClick={() => setCreating(!creating)}>{creating ? "Fermer" : "＋ Créer un cercle"}</button>
        </section>

        {creating && (
          <section className="group-create-panel">
            <div className="group-form-intro"><span>1</span><div><strong>Donnez-lui un nom clair</strong><p>Ex. Parents et sœurs, Cousins, Belle-famille.</p></div></div>
            <form onSubmit={submit}>
              <label>Nom du cercle<input value={name} onChange={(event) => setName(event.target.value)} placeholder="Ex. Belle-famille" minLength={2} maxLength={80} required /></label>
              <label>Description facultative<textarea value={description} onChange={(event) => setDescription(event.target.value)} placeholder="À quoi servira ce cercle ?" maxLength={240} rows={2} /></label>
              <div className="group-form-intro"><span>2</span><div><strong>Choisissez les personnes</strong><p>Vous êtes automatiquement responsable du cercle.</p></div></div>
              <MemberChoices selected={memberIds} onChange={setMemberIds} />
              {error && <p className="form-error">{error}</p>}
              <button className="primary-button full" disabled={saving}>{saving ? "Création…" : "Créer ce cercle"}</button>
            </form>
          </section>
        )}

        <section className="groups-list-section">
          <div className="section-heading"><div><h2>Mes cercles</h2><p>{groups.length} espace{groups.length > 1 ? "s" : ""} de partage</p></div></div>
          {loadError && <div className="error-panel">{loadError}</div>}
          {loading ? <div className="admin-loading">Chargement des cercles…</div> : groups.length ? (
            <div className="group-grid">
              {groups.map((group) => (
                <button className="group-card" key={group.id} onClick={() => navigate(`groups/${group.id}`)}>
                  <div className="group-avatar-stack">
                    {group.members.slice(0, 3).map((member) => <span key={member.profile.id}>{initials(member.profile.displayName)}</span>)}
                  </div>
                  <div><strong>{group.name}</strong><p>{group.description || "Un espace privé pour vos recettes."}</p><small>{group.members.length} membre{group.members.length > 1 ? "s" : ""}</small></div>
                  <span aria-hidden="true">›</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="empty-state group-empty"><span>◉</span><h3>Créez votre premier cercle</h3><p>Les recettes des parents peuvent rester entre parents, celles des cousins entre cousins.</p><button className="primary-button" onClick={() => setCreating(true)}>Créer un cercle</button></div>
          )}
        </section>
      </main>
      <BottomNav current="groups" />
    </>
  );
}

function GroupDetailPage({ groupId }: { groupId: string }) {
  const { user } = useAuth();
  const { groups, loading, updateGroupMembers, deleteGroup } = useGroups();
  const { recipes } = useRecipes();
  const group = groups.find((item) => item.id === groupId);
  const [editing, setEditing] = useState(false);
  const [memberIds, setMemberIds] = useState<string[]>(group?.members.filter((member) => member.role !== "owner").map((member) => member.profile.id) ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (group) {
      setMemberIds(group.members.filter((member) => member.role !== "owner").map((member) => member.profile.id));
    }
  }, [group]);

  if (loading) {
    return <><AppHeader title="Cercle" back="groups" /><main className="page"><div className="admin-loading">Chargement du cercle…</div></main></>;
  }

  if (!group) {
    return <><AppHeader title="Cercle" back="groups" /><main className="page not-found"><span>◉</span><h1>Cercle introuvable</h1><p>Vous n’en êtes peut-être plus membre.</p></main></>;
  }
  const isOwner = group.createdBy === user?.id;
  const groupRecipes = recipes.filter((recipe) => recipe.sharedGroups.some((share) => share.id === group.id));

  return (
    <>
      <AppHeader title={group.name} back="groups" />
      <main className="page group-detail-page">
        <section className="group-detail-hero">
          <div className="group-avatar-stack large">{group.members.slice(0, 4).map((member) => <span key={member.profile.id}>{initials(member.profile.displayName)}</span>)}</div>
          <div><p className="eyebrow">Cercle privé</p><h1>{group.name}</h1><p>{group.description || "Les recettes partagées ici restent entre les membres de ce cercle."}</p></div>
        </section>
        <section className="group-members-panel">
          <div className="section-heading"><div><h2>Membres</h2><p>{group.members.length} personne{group.members.length > 1 ? "s" : ""} ont accès</p></div>{isOwner && <button className="secondary-button" onClick={() => setEditing(!editing)}>{editing ? "Annuler" : "Modifier"}</button>}</div>
          <div className="member-chip-row">{group.members.map((member) => <span key={member.profile.id}><span className="mini-avatar">{initials(member.profile.displayName)}</span>{member.profile.displayName}{member.role === "owner" && <small>Responsable</small>}</span>)}</div>
          {editing && (
            <div className="group-member-editor">
              <MemberChoices selected={memberIds} onChange={setMemberIds} />
              {error && <p className="form-error">{error}</p>}
              <button
                className="primary-button"
                disabled={saving}
                onClick={async () => {
                  setSaving(true);
                  setError("");
                  try {
                    await updateGroupMembers(group.id, memberIds);
                    setEditing(false);
                  } catch (caught) {
                    setError(caught instanceof Error ? caught.message : "La modification a échoué.");
                  } finally {
                    setSaving(false);
                  }
                }}
              >{saving ? "Enregistrement…" : "Enregistrer les membres"}</button>
            </div>
          )}
        </section>
        <RecipeSection title="Recettes de ce cercle" subtitle={`${groupRecipes.length} recette${groupRecipes.length > 1 ? "s" : ""} partagée${groupRecipes.length > 1 ? "s" : ""} ici`} recipes={groupRecipes} empty="Aucune recette n’a encore été partagée dans ce cercle." tone="discover" compact />
        <button className="primary-button group-add-recipe" onClick={() => navigate("edit")}>＋ Ajouter une recette à partager</button>
        {isOwner && (
          <button
            className="text-button danger-text group-delete"
            onClick={async () => {
              if (!window.confirm(`Supprimer le cercle « ${group.name} » ? Les recettes resteront dans les carnets de leurs auteurs.`)) return;
              await deleteGroup(group.id);
              navigate("groups");
            }}
          >Supprimer ce cercle</button>
        )}
      </main>
    </>
  );
}

const demoAllowedUsers: AllowedUser[] = [
  {
    email: "claire@demo.mijote.fr",
    active: true,
    createdAt: "2026-07-20T10:00:00.000Z",
    consumedAt: "2026-07-20T10:05:00.000Z",
  },
  {
    email: "mamie.jeanne@demo.mijote.fr",
    active: true,
    createdAt: "2026-07-22T15:30:00.000Z",
  },
];

function formatInvitationDate(value: string) {
  return new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium" }).format(new Date(value));
}

function AdminPage() {
  const { user, isAdmin, isDemo } = useAuth();
  const [invitations, setInvitations] = useState<AllowedUser[]>(isDemo ? demoAllowedUsers : []);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(!isDemo);
  const [submitting, setSubmitting] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const refresh = useCallback(async () => {
    if (isDemo) return;
    setLoading(true);
    try {
      setInvitations(await listAllowedUsers());
      setError("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Impossible de charger les invitations.");
    } finally {
      setLoading(false);
    }
  }, [isDemo]);

  useEffect(() => {
    if (isAdmin) void refresh();
  }, [isAdmin, refresh]);

  if (!isAdmin) {
    return (
      <>
        <AppHeader title="Administration" back="" />
        <main className="page not-found">
          <span>🔒</span>
          <h1>Accès réservé</h1>
          <p>Seul l’administrateur de Mijoté peut gérer les invitations.</p>
          <button className="primary-button" onClick={() => navigate()}>Retour à l’accueil</button>
        </main>
      </>
    );
  }

  async function addInvitation(event: FormEvent) {
    event.preventDefault();
    const normalized = normalizeInviteEmail(email);
    const validationError = validateInviteEmail(normalized);
    setError(validationError);
    setSuccess("");
    if (validationError) return;
    setSubmitting(true);
    try {
      if (isDemo) {
        setInvitations((current) => [
          { email: normalized, active: true, createdAt: new Date().toISOString() },
          ...current.filter((item) => item.email !== normalized),
        ]);
      } else {
        await inviteAllowedUser(normalized);
        await refresh();
      }
      setEmail("");
      setSuccess(`${normalized} peut maintenant créer son compte.`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "L’invitation n’a pas pu être ajoutée.");
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleInvitation(invitation: AllowedUser) {
    const nextActive = !invitation.active;
    setPendingEmail(invitation.email);
    setError("");
    setSuccess("");
    try {
      if (isDemo) {
        setInvitations((current) =>
          current.map((item) => item.email === invitation.email ? { ...item, active: nextActive } : item),
        );
      } else {
        await setAllowedUserActive(invitation.email, nextActive);
        await refresh();
      }
      setSuccess(nextActive ? `${invitation.email} est de nouveau autorisée.` : `${invitation.email} a été révoquée.`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "La modification a échoué.");
    } finally {
      setPendingEmail("");
    }
  }

  async function shareAppLink() {
    const shareData = {
      title: "Rejoins notre carnet Mijoté",
      text: "Ton adresse est autorisée. Ouvre Mijoté puis choisis « J’ai reçu une invitation » pour créer ton compte.",
      url: "https://mijote-cmg.pages.dev/",
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
      await navigator.clipboard.writeText(shareData.url);
      setSuccess("Lien copié. Vous pouvez maintenant l’envoyer à votre proche.");
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === "AbortError") return;
      setError("Impossible de partager automatiquement. Utilisez mijote-cmg.pages.dev.");
    }
  }

  return (
    <>
      <AppHeader title="Invitations" back="profile" />
      <main className="page admin-page">
        <section className="admin-hero">
          <p className="eyebrow">Administration familiale</p>
          <h1>Qui peut rejoindre Mijoté&nbsp;?</h1>
          <p>Ajoutez d’abord l’adresse de votre proche. Il pourra ensuite créer son compte avec cette même adresse.</p>
        </section>

        {isDemo && <div className="demo-ribbon">Aperçu local · ces invitations ne sont pas envoyées</div>}

        <section className="admin-invite-card">
          <div>
            <h2>Autoriser une adresse</h2>
            <p className="muted">Aucun courriel n’est envoyé automatiquement : partagez ensuite le lien de l’application.</p>
          </div>
          <form className="admin-invite-form" onSubmit={addInvitation}>
            <label>
              Adresse électronique
              <input
                type="email"
                inputMode="email"
                autoCapitalize="none"
                autoComplete="email"
                placeholder="proche@exemple.fr"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </label>
            <button className="primary-button" type="submit" disabled={submitting}>
              {submitting ? "Ajout…" : "Autoriser cette adresse"}
            </button>
          </form>
          <button className="secondary-button share-invite-button" type="button" onClick={() => void shareAppLink()}>
            Partager le lien d’inscription
          </button>
          {success && <p className="success-text" role="status">✓ {success}</p>}
          {error && <p className="form-error" role="alert">{error}</p>}
        </section>

        <section className="invitation-list-section">
          <div className="section-heading">
            <div>
              <h2>Adresses autorisées</h2>
              <p>{invitations.length} adresse{invitations.length > 1 ? "s" : ""} dans la famille</p>
            </div>
          </div>
          {loading ? (
            <div className="admin-loading" role="status">Chargement des invitations…</div>
          ) : invitations.length ? (
            <div className="invitation-list">
              {invitations.map((invitation) => {
                const state = invitationState(invitation);
                const isOwner = invitation.email === user?.email.toLowerCase();
                return (
                  <article className="invitation-row" key={invitation.email}>
                    <div className="invitation-avatar" aria-hidden="true">{invitation.email.slice(0, 1).toUpperCase()}</div>
                    <div className="invitation-copy">
                      <strong>{invitation.email}</strong>
                      <span>Ajoutée le {formatInvitationDate(invitation.createdAt)}</span>
                    </div>
                    <span className={`invitation-status ${state}`}>
                      {state === "joined" ? "Compte créé" : state === "pending" ? "En attente" : "Révoquée"}
                    </span>
                    <button
                      className={invitation.active ? "danger-button compact-action" : "secondary-button compact-action"}
                      type="button"
                      disabled={isOwner || pendingEmail === invitation.email}
                      title={isOwner ? "Le compte administrateur ne peut pas être révoqué" : undefined}
                      onClick={() => void toggleInvitation(invitation)}
                    >
                      {pendingEmail === invitation.email ? "Un instant…" : invitation.active ? "Révoquer" : "Réautoriser"}
                    </button>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="empty-state"><span>✉</span><p>Aucune adresse autorisée pour le moment.</p></div>
          )}
        </section>
      </main>
    </>
  );
}

function ProtectedApp() {
  const route = useRoute();
  const { recipes } = useRecipes();
  if (route.name === "home") return <Dashboard />;
  if (route.name === "profile") return <ProfilePage />;
  if (route.name === "my-recipes") return <MyRecipesPage />;
  if (route.name === "categories") return <CategoryPage />;
  if (route.name === "groups") return <GroupsPage />;
  if (route.name === "group") return <GroupDetailPage groupId={route.id} />;
  if (route.name === "admin") return <AdminPage />;
  const recipe = "id" in route ? recipes.find((item) => item.id === route.id) : undefined;
  if (route.name === "edit") return <RecipeForm existing={recipe} />;
  if (!recipe) {
    return (
      <>
        <AppHeader back="" />
        <main className="page not-found"><span>🍽️</span><h1>Recette introuvable</h1><p>Elle a peut-être été supprimée ou n’est plus accessible.</p><button className="primary-button" onClick={() => navigate()}>Retour à l’accueil</button></main>
      </>
    );
  }
  if (route.name === "cook") return <KitchenMode recipe={recipe} />;
  return <RecipeDetail recipe={recipe} />;
}

function App() {
  const { user, loading, recoveryMode } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <LoginPage />;
  if (recoveryMode) return <RecoveryPasswordPage />;
  return (
    <RecipeProvider>
      <ProtectedApp />
    </RecipeProvider>
  );
}

export default App;
